import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'large';
}

const cardBgColors = [
  'bg-primary-50',
  'bg-secondary-50',
  'bg-accent-50',
  'bg-background-100',
];

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const bgColor = cardBgColors[parseInt(article.id, 10) % cardBgColors.length];

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 bg-background-50 rounded-xl overflow-hidden hover:shadow-md transition-all duration-300 border border-background-200"
      >
        <div className="w-32 sm:w-40 md:w-48 h-28 sm:h-32 md:h-36 shrink-0 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 py-3 pr-4 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
              {article.categoryName}
            </span>
            <span className="text-xs text-foreground-400">{article.publishDate}</span>
          </div>
          <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="hidden sm:block text-xs text-foreground-500 mt-1 line-clamp-1">
            {article.summary}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatNumber(article.readCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line" /> {formatNumber(article.likeCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'large') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-2xl overflow-hidden bg-background-50 hover:shadow-lg transition-all duration-300 border border-background-200"
      >
        <div className="relative h-56 md:h-72 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white mb-2">
              {article.categoryName}
            </span>
            <h3 className="text-lg md:text-xl font-bold text-white line-clamp-2 group-hover:text-accent-300 transition-colors">
              {article.title}
            </h3>
          </div>
        </div>
        <div className="p-4 md:p-5">
          <p className="text-sm text-foreground-600 line-clamp-2">{article.summary}</p>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <img src={article.authorAvatar} alt={article.author} className="w-6 h-6 rounded-full" />
              <span className="text-xs text-foreground-500">{article.author}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" /> {formatNumber(article.readCount)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line" /> {formatNumber(article.likeCount)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className={`group block rounded-xl overflow-hidden ${bgColor} hover:shadow-md transition-all duration-300`}
    >
      <div className="relative h-44 md:h-52 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-foreground-800 backdrop-blur-sm">
            {article.categoryName}
          </span>
        </div>
      </div>
      <div className="p-3.5 md:p-4 bg-background-50">
        <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <div className="flex items-center justify-between mt-2.5">
          <span className="text-xs text-foreground-400">{article.publishDate}</span>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line" /> {formatNumber(article.readCount)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function formatNumber(n: number): string {
  if (n >= 10000) {
    return `${(n / 10000).toFixed(1)}w`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(n);
}